import { Alert, Box, Card, CardContent, CardHeader, Drawer, Grid, IconButton, styled } from '@ethings/ui-toolkit';
import type { Filter } from '@sisense/sdk-data';
import { FC, JSX, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DashboardModel, FiltersPanel, FiltersPanelConfig, useGetDashboardModel, WidgetByIdStyleOptions, WidgetsPanelColumn } from 'sisense-sdk-ui-next/core';
import { useSmallScreen } from '../../hooks';
import { addLocalStorageItem, getLocalStorageItem } from '../../util';
import { useBoolFlag, useNumberFlag } from '../flags';
import { Icon } from '../icons';
import { MiddleSpinner } from '../middle-spinner';
import { SisenseWidget } from './sisense-widget';
import { SisenseWrapper } from './sisense-wrapper';

type DashboardType = 'dashboards' | 'overview' | 'reports' | 'analytics';

enum FilteringType {
  All = 1,
  Specific = 2,
}

interface Widget {
  oid: string;
  title?: string;
  description?: string;
  widgetType?: string;
}

interface SisenseComposeDashboardProps {
  dashboardId: string;
  type: DashboardType;
}

interface SisenseComposeDashboardLayoutProps {
  dashboardId: string;
  type: DashboardType;
  variationFilter: number | undefined;
  dashboard: DashboardModel;
  layoutColumns: WidgetsPanelColumn[];
  widgetsById: Record<string, Widget>;
  isSmallScreen: boolean;
  responsiveSize: (percentage: number) => number;
}

interface PersistingFilters {
  filters: string | null;
  timeStamp: number;
}

interface PersistedComposeFilterSnapshot {
  guid?: string;
  dim?: string;
  state?: Record<string, unknown>;
}

const setPersistentFilters = (dashboardId: string, dashboardType: DashboardType, filters: PersistingFilters, filterType: FilteringType): void => {
  const key = filterType === FilteringType.All ? dashboardType : dashboardId;
  const currentStorage = getLocalStorageItem<Record<string, PersistingFilters>>('persisting-filters') || {};
  currentStorage[key] = filters;
  addLocalStorageItem('persisting-filters', currentStorage);
};

const getPersistentFilters = (dashboardId: string, dashboardType: DashboardType, filterType: FilteringType): PersistingFilters | null => {
  const key = filterType === FilteringType.All ? dashboardType : dashboardId;
  const currentStorage = getLocalStorageItem<Record<string, PersistingFilters>>('persisting-filters');
  return currentStorage?.[key] || null;
};

const toPlainSerializable = (value: unknown): unknown => {
  if (value === null || value === undefined) {
    return value;
  }

  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return value;
  }

  if (Array.isArray(value)) {
    return value.map(toPlainSerializable);
  }

  if (typeof value === 'function') {
    return undefined;
  }

  if (typeof value !== 'object') {
    return undefined;
  }

  const obj = value as Record<string, unknown>;
  const plain: Record<string, unknown> = {};
  for (const key of Object.getOwnPropertyNames(obj)) {
    const next = toPlainSerializable(obj[key]);
    if (next !== undefined) {
      plain[key] = next;
    }
  }
  return plain;
};

const safeSerialize = (value: unknown): unknown => {
  if (!value || typeof value !== 'object') {
    return undefined;
  }
  const maybeAny = value as Record<string, unknown>;
  const serializeFn = maybeAny.serialize;
  if (typeof serializeFn === 'function') {
    try {
      return toPlainSerializable((serializeFn as () => unknown)());
    } catch {
      return undefined;
    }
  }
  return undefined;
};

const extractFilterState = (filter: Filter): Record<string, unknown> => {
  const anyFilter = filter as unknown as Record<string, unknown>;
  const state: Record<string, unknown> = {};
  for (const key of Object.getOwnPropertyNames(anyFilter)) {
    if (key === 'attribute' || key === '__serializable' || key === 'composeCode') {
      continue;
    }
    const next = toPlainSerializable(anyFilter[key]);
    if (next !== undefined) {
      state[key] = next;
    }
  }
  return state;
};

const getJaqlFromFilter = (filter: Filter): unknown => {
  const maybeAny = filter as unknown as Record<string, unknown>;
  const jaql = maybeAny.jaql;
  if (typeof jaql === 'function') {
    try {
      return toPlainSerializable((jaql as () => unknown)());
    } catch {
      return undefined;
    }
  }

  if (jaql && typeof jaql === 'object') {
    return toPlainSerializable(jaql);
  }

  const rawAttribute = maybeAny.attribute;
  const attribute = (typeof rawAttribute === 'function'
    ? (() => {
      try {
        return (rawAttribute as () => unknown)();
      } catch {
        return undefined;
      }
    })()
    : rawAttribute) as Record<string, unknown> | undefined;

  const attributeJaql = attribute?.jaql;
  if (typeof attributeJaql === 'function') {
    try {
      return toPlainSerializable((attributeJaql as () => unknown)());
    } catch {
      return undefined;
    }
  }

  if (attributeJaql && typeof attributeJaql === 'object') {
    return toPlainSerializable(attributeJaql);
  }

  return jaql;
};

const getFilterDimKey = (filter: Filter): string | null => {
  const jaql = getJaqlFromFilter(filter) as Record<string, unknown> | undefined;
  const dim = jaql?.dim;
  if (typeof dim === 'string' && dim) {
    return dim;
  }

  const maybeAny = filter as unknown as Record<string, unknown>;
  const levels = maybeAny.levels;
  if (Array.isArray(levels)) {
    const levelDim = (levels[0] as Record<string, unknown> | undefined)?.dim;
    if (typeof levelDim === 'string' && levelDim) {
      return levelDim;
    }
  }

  return null;
};

const toPersistedComposeFilterSnapshot = (filter: Filter): PersistedComposeFilterSnapshot => {
  const anyFilter = filter as unknown as Record<string, unknown>;
  const attribute = anyFilter.attribute;
  const attributeObj = (typeof attribute === 'function' ? (() => {
    try {
      return (attribute as () => unknown)();
    } catch {
      return undefined;
    }
  })() : attribute);

  const serialized = safeSerialize(filter);
  const attributeSerialized = safeSerialize(attributeObj);

  const dimFromAttr = (attributeSerialized as Record<string, unknown> | undefined)?.jaql as Record<string, unknown> | undefined;
  const dimFromFilter = (serialized as Record<string, unknown> | undefined)?.jaql as Record<string, unknown> | undefined;
  const dim = (typeof dimFromAttr?.dim === 'string' && dimFromAttr.dim)
    ? dimFromAttr.dim
    : ((typeof dimFromFilter?.dim === 'string' && dimFromFilter.dim)
      ? dimFromFilter.dim
      : getFilterDimKey(filter));

  const state = extractFilterState(filter);
  const guid = (
    (state.config as Record<string, unknown> | undefined)?.guid ??
    (anyFilter.config as Record<string, unknown> | undefined)?.guid
  );

  return {
    guid: typeof guid === 'string' && guid ? guid : undefined,
    dim: dim ?? undefined,
    state,
  };
};

const applyPersistedSnapshotToFilter = (base: Filter, snapshot: PersistedComposeFilterSnapshot): Filter => {
  const baseAny = base as unknown as Record<string, unknown>;
  const state = snapshot.state;
  if (state && typeof state === 'object') {
    for (const [key, value] of Object.entries(state)) {
      baseAny[key] = value;
    }
  }

  return base;
};

const getFilterIdentityKey = (filter: Filter): string => {
  const maybeAny = filter as unknown as Record<string, unknown>;
  const oid = maybeAny.oid;
  const id = maybeAny.id;
  if (typeof oid === 'string' && oid) {
    return `oid:${oid}`;
  }
  if (typeof id === 'string' && id) {
    return `id:${id}`;
  }
  try {
    return `json:${JSON.stringify(filter)}`;
  } catch {
    return `obj:${String(filter)}`;
  }
};

const sanitizeFilters = (filters: Filter[]): Filter[] => {
  const seen = new Set<string>();
  const deduped: Filter[] = [];
  for (const filter of filters) {
    const key = getFilterIdentityKey(filter);
    if (seen.has(key)) {
      continue;
    }
    seen.add(key);
    deduped.push(filter);
  }
  return deduped;
};

const DEFAULT_WIDGET_HEIGHT = 300;
const GRID_COLUMNS = 12;
const FILTER_DRAWER_WIDTH = 320;
const FLOATING_TOGGLE_OFFSET = 20;

const WIDGET_STYLE_OPTIONS: WidgetByIdStyleOptions = {
  shadow: 'None',
  header: {
    renderToolbar: () => null,
  },
};

const FILTERS_PANEL_CONFIG: FiltersPanelConfig = {
  actions: {
    editFilter: { enabled: true },
    deleteFilter: { enabled: true },
  },
};

const toGridUnits = (percentage: number): number => {
  const span = Math.round((percentage / 100) * GRID_COLUMNS);
  if (!span) {
    return GRID_COLUMNS;
  }
  return Math.min(GRID_COLUMNS, Math.max(1, span));
};

const LayoutRoot = styled(Box)({
  display: 'flex',
  width: '100%',
  flex: 1,
  minHeight: 0,
  overflow: 'hidden',
  position: 'relative',
});

const MainContent = styled(Box)(({ theme }) => ({
  flex: '1 1 auto',
  minWidth: 0,
  minHeight: 0,
  padding: theme.spacing(3),
  overflow: 'auto',
  boxSizing: 'border-box',
}));

const FilterDrawerContainer = styled(Box, { shouldForwardProp: (prop) => prop !== 'open' })<{ open: boolean }>(({ theme, open }) => ({
  width: open ? FILTER_DRAWER_WIDTH : 0,
  height: '100%',
  flexShrink: 0,
  display: 'flex',
  flexDirection: 'column',
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflow: 'hidden',
}));

const StyledDrawer = styled(Drawer)(({ theme }) => ({
  height: '100%',
  '& .MuiDrawer-paper': {
    boxSizing: 'border-box',
    position: 'relative',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    paddingTop: theme.spacing(1),
  },
}));

const FullHeightCard = styled(Card)({
  flex: 1,
  minHeight: 0,
  display: 'flex',
  flexDirection: 'column',
  borderRadius: 0,
});

const ScrollableCardContent = styled(CardContent)({
  flexGrow: 1,
  overflow: 'visible',
});

const FloatingToggle = styled(IconButton, { shouldForwardProp: (prop) => prop !== 'open' })<{ open: boolean }>(
  ({ theme, open }) => ({
    position: 'fixed',
    top: '50%',
    right: open ? `${FILTER_DRAWER_WIDTH - FLOATING_TOGGLE_OFFSET}px` : '0px',
    transform: 'translateY(-50%)',
    zIndex: theme.zIndex.drawer + 1,
    backgroundColor: theme.palette.background.paper,
    boxShadow: theme.shadows[3],
    borderRadius: '50%',
    transition: 'none',
    '&:hover, &:active': {
      backgroundColor: theme.palette.background.paper,
    },
  }),
);

const SisenseComposeDashboardLayout: FC<SisenseComposeDashboardLayoutProps> = ({
  dashboardId,
  type,
  variationFilter,
  dashboard,
  layoutColumns,
  widgetsById,
  isSmallScreen,
  responsiveSize,
}): JSX.Element => {
  const [filters, setFilters] = useState<Filter[]>([]);
  const [filtersDrawerOpen, setFiltersDrawerOpen] = useState(!isSmallScreen);
  const initializedRef = useRef(false);

  useEffect(() => {
    initializedRef.current = false;
  }, [dashboardId]);

  useEffect(() => {
    if (!initializedRef.current && dashboard?.filters) {
      const filterType = (variationFilter ?? FilteringType.Specific) as FilteringType;

      const savedFilters = getPersistentFilters(dashboardId, type, filterType);
      if (savedFilters?.filters) {
        const filterAge = Date.now() - savedFilters.timeStamp;
        const isExpired = filterAge >= 24 * 60 * 60 * 1000;
        if (isExpired) {
          setPersistentFilters(dashboardId, type, { filters: null, timeStamp: Date.now() }, filterType);
          setFilters(sanitizeFilters(dashboard.filters as Filter[]));
          initializedRef.current = true;
          return;
        }

        try {
          const decoded = JSON.parse(atob(savedFilters.filters)) as unknown;
          if (Array.isArray(decoded)) {
            const snapshotsByGuid = new Map<string, PersistedComposeFilterSnapshot>();
            for (const snapshot of decoded as PersistedComposeFilterSnapshot[]) {
              const guid = snapshot?.guid;
              if (typeof guid === 'string' && guid) {
                snapshotsByGuid.set(guid, snapshot);
              }
            }

            const hydrated = (dashboard.filters as Filter[]).map((baseFilter) => {
              const baseAny = baseFilter as unknown as Record<string, unknown>;
              const baseGuid = (baseAny.config as Record<string, unknown> | undefined)?.guid;
              const snapshot = typeof baseGuid === 'string' && baseGuid ? snapshotsByGuid.get(baseGuid) : undefined;
              return snapshot ? applyPersistedSnapshotToFilter(baseFilter, snapshot) : baseFilter;
            });
            setFilters(sanitizeFilters(hydrated));
          } else {
            setPersistentFilters(dashboardId, type, { filters: null, timeStamp: Date.now() }, filterType);
            setFilters(sanitizeFilters(dashboard.filters as Filter[]));
          }
        } catch {
          setPersistentFilters(dashboardId, type, { filters: null, timeStamp: Date.now() }, filterType);
          setFilters(sanitizeFilters(dashboard.filters as Filter[]));
        }
      } else {
        setFilters(sanitizeFilters(dashboard.filters as Filter[]));
      }
      initializedRef.current = true;
    }
  }, [dashboard?.oid, dashboard?.filters, dashboardId, type, variationFilter]);

  const handleFilterChanged = (nextFilters: unknown): void => {
    const filterType = (variationFilter ?? FilteringType.Specific) as FilteringType;

    if (!Array.isArray(nextFilters)) {
      return;
    }

    const sanitized = sanitizeFilters(nextFilters as Filter[]);

    setFilters(sanitized);

    const persisted = sanitized.map(toPersistedComposeFilterSnapshot);
    setPersistentFilters(dashboardId, type, { filters: btoa(JSON.stringify(persisted)), timeStamp: Date.now() }, filterType);
  };

  const handleToggleDrawer = (): void => setFiltersDrawerOpen((open) => !open);

  return (
    <LayoutRoot>
      <FloatingToggle aria-label={filtersDrawerOpen ? 'Hide filters' : 'Show filters'} onClick={handleToggleDrawer} open={filtersDrawerOpen}>
        <Icon icon={filtersDrawerOpen ? 'ChevronRight' : 'ChevronLeft'} />
      </FloatingToggle>
      <MainContent>
        <Grid container spacing={2} key={`grid-${String(filtersDrawerOpen)}`}>
          {layoutColumns.map((column, columnIndex) => (
            <Grid key={`column-${columnIndex}`} size={responsiveSize(column.widthPercentage)}>
              <Grid container direction="column" spacing={2}>
                {column.rows.map((row, rowIndex) => (
                  <Grid key={`row-${columnIndex}-${rowIndex}`}>
                    <Grid container spacing={2} wrap={isSmallScreen ? 'wrap' : 'nowrap'}>
                      {row.cells.map((cell) => {
                        const widget = widgetsById[cell.widgetId];
                        if (!widget) { return null; }
                        return (
                          <Grid key={cell.widgetId} size={responsiveSize(cell.widthPercentage)}>
                            <Card>
                              {widget.widgetType !== 'text' && widget.title?.trim() && (
                                <CardHeader
                                  title={widget.title}
                                  subheader={widget.description || ''}
                                />
                              )}
                              <CardContent
                                style={{
                                  padding: 0,
                                  paddingBottom: 0,
                                }}
                              >
                                <SisenseWidget
                                  title=''
                                  dashboardOid={dashboard.oid}
                                  filters={filters}
                                  widgetOid={widget.oid}
                                  styleOptions={WIDGET_STYLE_OPTIONS}
                                  height={cell.height || DEFAULT_WIDGET_HEIGHT}
                                />
                              </CardContent>
                            </Card>
                          </Grid>
                        );
                      })}
                    </Grid>
                  </Grid>
                ))}
              </Grid>
            </Grid>
          ))}
        </Grid>
      </MainContent>
      <FilterDrawerContainer open={filtersDrawerOpen}>
        <StyledDrawer
          anchor="right"
          open={filtersDrawerOpen}
          variant={isSmallScreen ? 'temporary' : 'persistent'}
          onClose={handleToggleDrawer}
          PaperProps={{
            sx: {
              width: isSmallScreen ? '100vw' : FILTER_DRAWER_WIDTH,
            },
          }}
        >
          <FullHeightCard>
            <ScrollableCardContent>
              <FiltersPanel
                filters={filters}
                onFiltersChange={(nextFilters) => { handleFilterChanged(nextFilters); }}
                config={FILTERS_PANEL_CONFIG}
              />
            </ScrollableCardContent>
          </FullHeightCard>
        </StyledDrawer>
      </FilterDrawerContainer>
    </LayoutRoot>
  );
};

const SisenseComposeDashboardInternal: FC<SisenseComposeDashboardProps> = ({ dashboardId, type }): JSX.Element => {
  const isSmallScreen = useSmallScreen();
  const composeSdkEnabled = useBoolFlag('portals-sisense-compose-sdk-feature');
  const variationFilter = useNumberFlag('track-dashboard-persisting-variation-filters');
  const { dashboard, isError, error, isLoading } = useGetDashboardModel({
    dashboardOid: dashboardId,
    includeWidgets: true,
    includeFilters: true,
    enabled: true,
  });

  const widgetsById = useMemo(
    () => (dashboard?.widgets ?? []).reduce((acc, widget) => ({
      ...acc,
      [widget.oid]: {
        oid: widget.oid,
        title: widget.title,
        description: widget.description,
        widgetType: widget.widgetType,
      },
    }), {} as Record<string, Widget>),
    [dashboard?.widgets],
  );

  const responsiveSize = useCallback(
    (percentage: number): number => (isSmallScreen ? GRID_COLUMNS : toGridUnits(percentage)),
    [isSmallScreen],
  );

  if (isLoading) {
    return <MiddleSpinner />;
  }

  if (isError && error) {
    return <Alert severity='error'>{error.message}</Alert>;
  }

  const layoutColumns = dashboard?.layoutOptions.widgetsPanel?.columns;

  if (!composeSdkEnabled || !dashboard?.widgets || !layoutColumns) {
    return <></>;
  }

  return (
    <SisenseComposeDashboardLayout
      dashboardId={dashboardId}
      type={type}
      variationFilter={variationFilter}
      dashboard={dashboard}
      layoutColumns={layoutColumns}
      widgetsById={widgetsById}
      isSmallScreen={isSmallScreen}
      responsiveSize={responsiveSize}
    />
  );
};

export const SisenseComposeDashboard: FC<SisenseComposeDashboardProps> = (props): JSX.Element => (
  <SisenseWrapper>
    <SisenseComposeDashboardInternal {...props} />
  </SisenseWrapper>
);
